import { useEffect, useState } from "react";
import { IPurchaseRequisition } from "@/types/purchaseRequisition.type";
import {
  DraftOrder,
  getPurchaseRequisitionById,
  validatePurchaseRequisition,
} from "@/common/services/purchaseRequisition.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { useGetStockProductDetails } from "@/common/hooks";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { ModalQuotedPurchaseRequisition } from "./ModalQuotedPurchaseRequisition";

type Props = {
  data: IPurchaseRequisition;
  onClose: () => void;
  onApproved?: () => void;
  action: string;
};

export type ApprovedRow = {
  productId?: string;
  productName?: string;
  quantity?: string;
  approvedQuantity?: string;
  deliveryDate?: string | undefined;
  quoteValue: string | undefined;
  quoted: boolean;
  status?: string;
};

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: "Alta",
  MEDIUM: "Média",
  LOW: "Baixa",
};

const ItemRow = ({ item, idx }) => {
  const { data } = useGetStockProductDetails(item.productId);
  const { details } = data || {};

  return (
    <tr key={`${item.productId}-${idx}`}>
      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
      <td className="px-4 py-3 min-w-0">
        <div className="font-medium text-gray-900 truncate">
          {item.productName}
        </div>
        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-2">
          <span>({details?.[0].quantity || 0} no estoque atual)</span>
          <span>
            (Última compra:{" "}
            {details?.[0]?.createdAt
              ? new Date(details[0].createdAt)?.toLocaleDateString("pt-BR")
              : "Sem registro"}
            )
          </span>
          <span>
            (Valor da última compra:{" "}
            {formatCurrency(details?.[0]?.sellPrice || 0)})
          </span>
        </div>
      </td>
      <td className="px-4 py-3 text-center font-semibold text-gray-800">
        {item.quoted ? "Sim" : "Não"}
      </td>
      <td className="px-4 py-3 text-center font-semibold text-gray-800">
        {item.quantity}
      </td>
    </tr>
  );
};

export default function ModalValidatePurchaseRequisition({
  data,
  onClose,
  onApproved,
  action,
}: Props) {
  const { notifySuccess, notifyError } = useToastContext();
  const [purchaseRequisition, setPurchaseRequisition] =
    useState<IPurchaseRequisition | null>(null);
  const [rows, setRows] = useState<Record<number, ApprovedRow>>({});
  const [isQuotedModalOpen, setIsQuotedModalOpen] = useState(false);

  const openQuotedModal = () => setIsQuotedModalOpen(true);
  const closeQuotedModal = () => setIsQuotedModalOpen(false);

  const toInputDate = (d?: string | Date | null) => {
    if (!d) return "";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "";
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const labelModal = action === "validated" ? "Validar" : "Aprovar";
  const isApprovedAction = action === "approved";

  useEffect(() => {
    const load = async () => {
      if (!data._id) return;
      const res = await getPurchaseRequisitionById(data._id);
      const pr = res.data as IPurchaseRequisition;
      setPurchaseRequisition(pr);
      const next: Record<number, ApprovedRow> = {};
      pr.itens.forEach((it, idx) => {
        next[idx] = {
          quantity: String(it.quantity ?? "1"),
          deliveryDate: toInputDate(it.deliveryDate ?? null),
          quoteValue: (it as unknown as ApprovedRow)?.quoteValue,
          quoted: (it as unknown as ApprovedRow)?.quoted || false,
          status: (it as unknown as ApprovedRow).status,
        };
      });
      setRows(next);
    };
    load();
  }, [data._id, isApprovedAction]);

  const handleSave = async () => {
    if (!purchaseRequisition) return;

    const invalidQuoteIdx = purchaseRequisition.itens.findIndex((_, idx) => {
      const r = rows[idx];
      const val = r?.quoteValue ? Number(r.quoteValue) : 0;
      return r?.quoted && !(Number.isFinite(val) && val > 0);
    });
    if (invalidQuoteIdx !== -1) {
      notifyError(
        `Informe um valor de cotação válido (> 0) para o item #${
          invalidQuoteIdx + 1
        }.`
      );
      return;
    }

    const approvedItens = purchaseRequisition.itens.map((it, idx) => {
      const itemData = {
        productId: it.productId,
        quantity: rows[idx]?.quantity ?? it.quantity ?? 1,
        deliveryDate: rows[idx]?.deliveryDate
          ? new Date(rows[idx].deliveryDate || "").toISOString()
          : undefined,
        quoteValue: rows[idx]?.quoteValue ? rows[idx].quoteValue : undefined,
        quoted: rows[idx]?.quoted,
      };

      return itemData;
    });

    if (approvedItens.some((i) => Number(i.quantity) < 1)) {
      notifyError("Quantidade aprovada inválida.");
      return;
    }

    try {
      const res = await validatePurchaseRequisition(purchaseRequisition._id, {
        ...data,
        items: approvedItens,
      });
      if (res.ok) {
        notifySuccess("Requisição validada com sucesso.");
        onApproved?.();
        onClose();
      } else {
        notifyError(res.message as string);
      }
    } catch (e) {
      notifyError((e as Error)?.message ?? "Erro ao Validar.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4">
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white z-10 w-full max-w-7xl rounded-2xl shadow-2xl p-8 relative overflow-y-auto max-h-[90vh] border border-gray-200"
      >
        <button
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-2xl font-bold transition"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-3">
          {labelModal} Requisição de Compra
        </h2>

        {!purchaseRequisition ? (
          <div className="py-12 text-center text-gray-600">Carregando...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">Data</p>
                <span className="w-full">
                  {new Date(purchaseRequisition.date)?.toLocaleDateString()}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Solicitante
                </p>
                <span className="w-full">
                  {purchaseRequisition.requestorName}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Placa
                </p>
                <span className="w-full">
                  {purchaseRequisition.licensePlate || "N/A"}
                </span>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Prioridade
                </p>
                <span className="w-full">
                  {PRIORITY_LABELS[purchaseRequisition.priority ?? ""] ||
                    purchaseRequisition.priority ||
                    ""}
                </span>
              </div>
            </div>
            <div className="mb-8">
              <p className="text-sm font-semibold mb-1 text-gray-700">
                Observações
              </p>
              <span className="w-full">
                {purchaseRequisition.observation || "Nenhuma observação."}
              </span>
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Itens</h3>
            {purchaseRequisition.itens?.length ? (
              <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-[45vh]">
                  <div className="px-4 py-3 text-right font-semibold text-black-800">
                    <button
                      className="mt-2 w-50 font-semibold transition py-2 px-4 rounded-md bg-greenDark text-white hover:bg-green-700"
                      onClick={openQuotedModal}
                    >
                      Gerar Cotação
                    </button>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs font-semibold text-gray-600">
                      <tr>
                        <th className="px-4 py-2 text-left">#</th>
                        <th className="px-4 py-2 text-left">Item</th>
                        <th className="px-4 py-2 text-center">Cotado?</th>
                        <th className="px-4 py-2 text-center">
                          Qtd. Solicitada
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {purchaseRequisition.itens
                        .filter((purchase) =>
                          isApprovedAction
                            ? (purchase as unknown as ApprovedRow)?.quoted
                            : true
                        )
                        .map((it, idx) => (
                          <ItemRow
                            key={`${it.productId}-${idx}`}
                            item={it}
                            idx={idx}
                          />
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 text-sm text-gray-600 mb-6">
                Nenhum item informado.
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="mt-2 w-32 font-semibold transition py-2 px-4 rounded-md border bg-gray5 text-gray-700 hover:bg-gray-100"
              >
                Cancelar
              </button>
              &nbsp;&nbsp;
              <button
                onClick={handleSave}
                className="mt-2 w-56 font-semibold transition py-2 px-4 rounded-md bg-locavibe text-white hover:bg-red-700"
              >
                Salvar
              </button>
            </div>
          </>
        )}
      </div>
      {isQuotedModalOpen && (
        <ModalQuotedPurchaseRequisition
          onClose={closeQuotedModal}
          requisition={
            purchaseRequisition as IPurchaseRequisition & {
              budgets: DraftOrder[];
            }
          }
        />
      )}
    </div>
  );
}
